import React, { useState, useEffect } from 'react';
import cookie from 'react-cookies'
import useMediaQuery from '@mui/material/useMediaQuery'
import { formatMoney, formatDiscountPercent } from '../../helpers/utils'
import { Table } from 'react-bootstrap'
import DetailsSummarySection from '../../components/details/summary/DetailsSummarySection'
import TotalsSummaryCard from '../../components/TotalsSummaryCard'
import {
  buildOrderFields,
  buildOrderSummaryFields,
  getOrderPaymentAttachmentValues
} from '../helpers/orderDetailsHelpers'
import ClientHeader from '../../components/ClientHeader/ClientHeader'
import DetailStatusMobileHeader from '../../components/DetailStatusMobileHeader'
import PaymentAttachmentsCard from '../../components/details/attachments/PaymentAttachmentsCard'
import { ORDER_HEADER_CONFIG } from '../constants/orderConstants'
import { layoutMobileMediaQuery } from '../../layout/layoutConstants'
import OrderDetailsTabMobileLines from '../mobile/OrderDetailsTabMobileLines'

const OrderDetailsTab = ({
  orderDetails: initialOrderDetails,
  transactionUuid,
  statusUI,
  showStatusInHeader = false,
}) => {
  const [orderDetails, setOrderDetails] = useState(initialOrderDetails || []);
  const [customFields, setCustomFields] = useState(initialOrderDetails?.custom_fields || []);
  const [taxSettings, setTaxSettings] = useState(cookie.load('tax_settings'));
  const [parsedDetails, setParsedDetails] = useState([]);
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery);

  useEffect(() => {
    setOrderDetails(initialOrderDetails || []);
    setCustomFields(initialOrderDetails?.custom_fields || []);

    const rawDetails = initialOrderDetails?.details;
    if (!Array.isArray(rawDetails)) {
      setParsedDetails([]);
      return;
    }

    const detailsWithParsedFields = rawDetails.map((detail) => {
      let parsedFields = [];

      if (detail.custom_fields_item) {
        if (Array.isArray(detail.custom_fields_item)) {
          parsedFields = detail.custom_fields_item.map((field) => ({
            ...field,
            value: field.value ?? '-',
          }));
        } else if (typeof detail.custom_fields_item === "string" && detail.custom_fields_item !== "[]" && detail.custom_fields_item.trim() !== "") {
          try {
            parsedFields = JSON.parse(detail.custom_fields_item).map((field) => ({
              ...field,
              value: field.value ?? '-',
            }));
          } catch (error) {
            console.error("Error al parsear custom_fields_item:", error, detail.custom_fields_item);
          }
        }
      }

      return { ...detail, parsedFields };
    });

    setParsedDetails(detailsWithParsedFields);
  }, [initialOrderDetails]);

  const customFieldKeys = Array.from(
    new Set(parsedDetails.flatMap((detail) => detail.parsedFields.map((f) => f.label)))
  );

  const calculateTaxRate = () => {
    let total = 0;
    if (orderDetails.origin_of_order === 'UbiquaApp') {
      orderDetails.details.forEach(
        (obj) => (total += (parseFloat(obj.extended_price) * parseFloat(obj.tax_rate)) / 100)
      );
    } else {
      orderDetails.details.forEach(
        (obj) => (total += parseFloat(obj.extended_price) * parseFloat(obj.tax_rate))
      );
    }
    return total;
  };

  const calculateTaxRate2 = () => {
    let total = 0;
    orderDetails.details.forEach(
      (obj) => (total += (parseFloat(obj.extended_price) * parseFloat(obj.tax_rate_2)) / 100)
    );
    return total;
  };

  const calculateTotal = () => {
    if (orderDetails.origin_of_order === 'UbiquaApp') {
      return Number(orderDetails.total);
    }
    let total = 0;
    orderDetails.details.forEach((obj) => {
      const calculatedTaxPrice = obj.calculated_tax_price
        ? obj.calculated_tax_price
        : obj.extended_price;
      total += parseFloat(calculatedTaxPrice);
    });
    return total;
  };

  const summaryFields = buildOrderSummaryFields(orderDetails)
  const fieldsToRender = buildOrderFields(orderDetails, customFields)
  const paymentAttachments = getOrderPaymentAttachmentValues(customFields)
  const totalRows = [
    {
      label: 'SUB-TOTAL:',
      value: formatMoney(orderDetails.subtotal)
    },
    {
      label: 'DESCUENTOS:',
      value: `-${formatMoney(orderDetails.discount || 0)}`,
      negative: true
    },
    taxSettings.tax_rate_2 && {
      label: `${taxSettings.tax_rate_2}:`,
      value: formatMoney(calculateTaxRate2())
    },
    {
      label: `${taxSettings.tax_rate}:`,
      value: formatMoney(calculateTaxRate())
    },
    {
      label: 'TOTAL:',
      value: formatMoney(calculateTotal()),
      strong: true,
      dividerBefore: true
    }
  ]

  return (
    <div className="container-fluid px-0">
      <div>
        {showStatusInHeader ? (
          <DetailStatusMobileHeader
            statusUI={statusUI}
            fallbackStatus={orderDetails?.status}
          />
        ) : (
          <div>
            <div className="show-product-header">
              <h3 className="margin-text-subtitle">Detalles del pedido:</h3>
            </div>
            <hr />
          </div>
        )}
        <div>
          <ClientHeader
            data={orderDetails}
            config={ORDER_HEADER_CONFIG}
          />
          <hr />
          <div>
            <DetailsSummarySection
              summaryFields={summaryFields}
              extraFields={fieldsToRender}
              summaryTitle="DETALLES DEL PEDIDO"
              summaryOpenLabel="Abrir detalles del pedido"
              summaryCloseLabel="Cerrar detalles del pedido"
            />
            <PaymentAttachmentsCard
              images={paymentAttachments.images}
              files={paymentAttachments.files}
              title="ADJUNTO(S)"
              emptyDescription="Cuando este pedido tenga imágenes o archivos asociados, aparecerán aquí para su revisión."
            />
            <hr />
            <div className="mr-85 mb-10">
              <div>
                <b>Productos en Pedido:</b>
              </div>
            </div>
            {isMobileLayout ? (
              <OrderDetailsTabMobileLines parsedDetails={parsedDetails} customFieldKeys={customFieldKeys} />
            ) : (
              <Table hover responsive>
                <thead className="table-header-gray-mui">
                  <tr>
                    <th>CÓDIGO</th>
                    <th>NOMBRE DEL PRODUCTO</th>
                    <th>UNIDAD DE VENTA</th>
                    <th>CANTIDAD</th>
                    <th>PRECIO</th>
                    <th>% DESCUENTO</th>
                    <th>SUB-TOTAL</th>
                    {customFieldKeys.map((field) => (
                      <th key={field} className="column-custom">{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedDetails && parsedDetails.length > 0 ? (
                    parsedDetails.map((model, index) => (
                      <tr key={index}>
                        <td className="order-td-text">{model.product_db_ref}</td>
                        <td className="order-td-text">{model.product_name}</td>
                        <td className="order-td-text">{model.price_unit}</td>
                        <td className="order-td-money">{Number(model.quantity).toFixed(2)}</td>
                        <td className="order-td-money">{formatMoney(Number(model.unit_price))}</td>
                        <td className="order-td-money">{formatDiscountPercent(model.discount)}</td>
                        <td className="order-td-money">{formatMoney(model.extended_price)}</td>

                        {customFieldKeys.map((field) => {
                          const fieldData = model.parsedFields.find((f) => f.label === field);
                          return (
                            <td key={field} className="column-custom">
                              {fieldData?.value ?? "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7 + customFieldKeys.length} className="order-td-text">
                        No hay resultados
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
            <TotalsSummaryCard rows={totalRows} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsTab;
